import { SoftwareCostOut, SoftwareOut, SoftwareUserCount } from '@/client'
import {
  deserializeApplication,
  serializeApplicationCosts,
  deserializeApplicationCosts,
  serializeApplication,
  serializeApplicationUserCount,
  ApplicationTable,
  createApplicationTable,
  createApplicationCostsTable,
  createApplicationUserCountTable,
} from './application.models'
import { db } from '../database.js'
import { createUpdateSet, deepUnwrap } from '../util'
import { CacheStateRepository, CacheStateTable } from '../cacheState.store'
import dayjs from 'dayjs'
import {
  _addApplication,
  _deleteApplication,
  _getApplicationById,
  _getApplicationCosts,
  _getApplications,
  _getApplicationUserCount,
  _putApplication,
} from './application.requests'
import {
  ApplicationFilter,
  applyApplicationFilters,
} from './application.filter'
import { usePreferenceStore } from '../preferenceStore'
import { sql } from 'kysely'

const lastCacheChange = ref<Date>()
const lastCostsCacheChange = ref<Date>()

const totalAppCosts = ref<number>()

type Pagination = {
  limit?: number
  offset?: number
}

type Sort = {
  column?: keyof ApplicationTable
  direction?: 'asc' | 'desc'
}

export type EnrichedSoftwareOut = SoftwareOut & {
  user_count: number
  subaccount_user_count: Record<string, number>
  application_costs: number
  sub_account_costs: Record<string, number>
  key: string
}

export const ApplicationStore = {
  lastCacheChange,
  lastCostsCacheChange,

  async bulkInsertSoftwareOut(
    applications: SoftwareOut[],
    batchSize: number = 1000
  ) {
    const serializedLicences = applications.map(serializeApplication)

    await db.transaction().execute(async (trx) => {
      for (let i = 0; i < serializedLicences.length; i += batchSize) {
        const batch = serializedLicences.slice(i, i + batchSize)
        await trx
          .insertInto('application')
          .values(batch)
          .onConflict((oc) =>
            oc.column('_id').doUpdateSet(createUpdateSet(serializedLicences[0]))
          )
          .execute()
      }
    })

    if (applications.length > 0) {
      lastCacheChange.value = new Date()
    }
    return applications
  },

  async refreshApplicationCacheFromApi(filter: ApplicationFilter = {}) {
    const results = await _getApplications({
      updatedGte: filter.updatedAfter,
      includeDeleted: true,
      excludeLicenceGroups: true,
    })

    if (!results.error && results.data) {
      await this.bulkInsertSoftwareOut(results.data as SoftwareOut[])
    }
    return results
  },

  async updateApplicationCacheFromApi(fullReload: boolean = false) {
    let isFullReload = true

    const lastRefresh =
      await CacheStateRepository.getCacheStateById('application')

    const filter: ApplicationFilter = {}
    if (lastRefresh && lastRefresh.lastUpdate && !fullReload) {
      filter.updatedAfter = lastRefresh.lastUpdate
      isFullReload = false
    }

    // Update updated licenses
    await this.refreshApplicationCacheFromApi(filter)

    type UpdateCacheStateTable = Partial<Omit<CacheStateTable, 'id'>> & {
      id: string
    }
    const update: UpdateCacheStateTable = {
      id: 'application',
      lastUpdate: dayjs().toISOString(),
    }

    if (isFullReload) {
      update.lastCompleteLoad = dayjs().toISOString()
    }

    await CacheStateRepository.upsertCacheState(update)
  },

  async getApplications<T>(
    filters: ApplicationFilter = { isDeleted: false },
    pagination: Pagination = {},
    sort: Sort = {},
    join: boolean = false
  ): Promise<T[]> {
    let query = db.selectFrom('application').selectAll()

    query = applyApplicationFilters(query, filters)

    if (join) {
      query = query
        .leftJoin(
          'application_usercount',
          'application._id',
          'application_usercount._id'
        )
        .select([
          'application_usercount.total as user_count',
          'application_usercount.sub_accounts as subaccount_user_count',
          'application.sub_accounts',
        ])

      query = query
        .leftJoin(
          'application_costs',
          'application._id',
          'application_costs._id'
        )
        .select([
          'application_costs.total as application_costs',
          'application_costs.sub_account_costs as sub_account_costs',
        ])

      query = query.select(sql<string>`application._id`.as('_id'))
      query = query.select(sql<string>`application._id`.as('key'))
    }

    if (pagination.limit) {
      query = query.limit(pagination.limit)
    }
    if (pagination.offset) {
      query = query.offset(pagination.offset)
    }
    if (sort.column) {
      query = query.orderBy(sort.column, sort.direction ?? 'asc')
    }

    const results = await query.execute()

    return results.map((result) => deserializeApplication(result, join)) as T[]
  },

  async getApplicationsCount(
    filters: ApplicationFilter = { isDeleted: false }
  ): Promise<number> {
    const unwrappedFilters = deepUnwrap(filters)

    let query = db
      .selectFrom('application')
      .select(db.fn.count('_id').as('count'))
    query = applyApplicationFilters(query, unwrappedFilters)

    const result = await query.executeTakeFirst()
    return result ? Number(result.count) : 0
  },

  async getApplicationById(
    id: string,
    useFrontendCache: boolean = true, // Use SQLite Cache
    options: {
      excludeLicenceGroups?: boolean
      refetchFromApplication?: boolean
    } = { excludeLicenceGroups: true, refetchFromApplication: false }
  ) {
    const record = await db
      .selectFrom('application')
      .selectAll()
      .where('_id', '=', id)
      .executeTakeFirst()

    let result = record ? deserializeApplication(record) : record

    if (!record || !useFrontendCache || options.refetchFromApplication) {
      const response = await _getApplicationById({
        softwareId: id,
        cached: !options.refetchFromApplication,
        excludeLicenceGroups: options.excludeLicenceGroups,
        includeDeleted: true,
      })
      if (response.data) {
        result = response.data
        this.bulkInsertSoftwareOut([result])
      }
    }

    return result || null
  },

  async disconnectApplication(id: string) {
    return _deleteApplication({ softwareId: id }).then(async (response) => {
      await db
        .deleteFrom('application')
        .where('application._id', '=', id)
        .execute()
      lastCacheChange.value = new Date()
      return response
    })
  },

  useApplications<T>(
    initialFilters:
      | ApplicationFilter
      | Ref<ApplicationFilter>
      | ComputedRef<ApplicationFilter> = {},
    initialPagination:
      | Pagination
      | Ref<Pagination>
      | ComputedRef<Pagination> = {},
    initialSort: Sort | Ref<Sort> | ComputedRef<Sort> = {},
    join?: boolean
  ) {
    const data = ref<T[]>([])
    const isLoading = ref(true)

    const fetchData = async () => {
      const filters = deepUnwrap(initialFilters) as ApplicationFilter
      const pagination = deepUnwrap(initialPagination) as Pagination
      const sort = deepUnwrap(initialSort) as Sort
      data.value = await this.getApplications<T>(
        filters,
        pagination,
        sort,
        join
      )

      isLoading.value = false
    }

    const stopWatch = watch(
      [
        () => unref(initialFilters),
        () => unref(initialPagination),
        () => unref(initialSort),
        lastCacheChange,
      ],
      async () => {
        await fetchData()
      },
      { deep: true }
    )

    const updateParameters = (
      newFilters: Partial<ApplicationFilter> = {},
      newPagination: Partial<Pagination> = {},
      newSort: Partial<Sort> = {}
    ) => {
      const cloneAndMerge = <T extends object>(
        source: T | Ref<T>,
        updates: Partial<T>
      ): T => {
        if (isRef(source)) {
          return { ...source.value, ...updates }
        } else {
          return { ...source, ...updates }
        }
      }

      initialFilters = cloneAndMerge(initialFilters, newFilters)
      initialPagination = cloneAndMerge(initialPagination, newPagination)
      initialSort = cloneAndMerge(initialSort, newSort)

      fetchData()
    }

    fetchData()

    return {
      data,
      unsubscribe: stopWatch,
      updateParameters,
      loading: isLoading,
      lastCacheUpdate: lastCacheChange,
    }
  },

  useApplicationsCount(
    initialFilters:
      | ApplicationFilter
      | Ref<ApplicationFilter>
      | ComputedRef<ApplicationFilter> = {}
  ) {
    const count = ref<number>(0)

    const fetchCount = async () => {
      const filters = deepUnwrap(initialFilters) as ApplicationFilter
      count.value = await this.getApplicationsCount(filters)
    }

    const stopWatch = watch(
      [() => unref(initialFilters), lastCacheChange],
      async () => {
        await fetchCount()
      },
      { deep: true }
    )

    fetchCount()

    return {
      count,
      unsubscribe: stopWatch,
      lastCacheUpdate: lastCacheChange,
    }
  },

  async createApplication(
    newApplication: Parameters<typeof _addApplication>[0]
  ) {
    return _addApplication(newApplication).then(async (response) => {
      if (!response.error && response.data) {
        const serializedApplication = serializeApplication(response.data)
        await db
          .insertInto('application')
          .values(serializedApplication)
          .execute()
      }
      lastCacheChange.value = new Date()
      return response
    })
  },

  useApplicationByID(
    id: string | Ref<string | null> | ComputedRef<string | null> | null,
    useFrontendCache = true
  ) {
    const application = ref<SoftwareOut | null>(null)
    const loading = ref(false)
    const error = ref<Error | null>(null)

    const fetchApplication = async () => {
      try {
        const applicationID = unref(id) // Extract reactive or static ID
        if (id === null) {
          application.value = null
          return
        }

        loading.value = true

        if (!applicationID) {
          throw new Error('No ID provided for fetching the application.')
        }
        application.value = await ApplicationStore.getApplicationById(
          applicationID,
          useFrontendCache
        )
        error.value = null
      } catch (err) {
        application.value = null
        error.value = err instanceof Error ? err : new Error(String(err))
      } finally {
        loading.value = false
      }
    }

    // Watch for changes in the ID and refetch application
    const stopWatch = watch(
      () => unref(id),
      async () => {
        await fetchApplication()
      },
      { immediate: true }
    )

    return {
      application,
      loading,
      error,
      fetchApplication,
      unsubscribe: stopWatch,
    }
  },

  async updateApplication(
    id: string,
    update: Parameters<typeof _putApplication>[0]['requestBody']
  ) {
    const response = await _putApplication({
      softwareId: id,
      requestBody: update,
    })
    if (!response.error && response.data) {
      await this.bulkInsertSoftwareOut([response.data])
    }
    return response
  },

  // ----------------------------------------------------------------------
  // Application User Count
  // ----------------------------------------------------------------------

  async updateApplicationUserCountCacheFromApi() {
    await this.refreshApplicationUserCountCacheFromApi()
  },

  async refreshApplicationUserCountCacheFromApi() {
    const results = await _getApplicationUserCount({})

    if (!results.error && results.data && typeof results.data === 'object') {
      await this.bulkInsertApplicationUserCount(results.data)
    }

    return results
  },

  async bulkInsertApplicationUserCount(
    counts: Record<string, SoftwareUserCount>
  ) {
    const serializedCounts = Object.entries(counts).map(
      ([softwareId, count]) => ({
        ...serializeApplicationUserCount(softwareId, count),
      })
    )

    if (serializedCounts.length === 0) {
      return counts
    }

    await db
      .insertInto('application_usercount')
      .values(serializedCounts)
      .onConflict((oc) =>
        oc.column('_id').doUpdateSet(createUpdateSet(serializedCounts[0]))
      )
      .execute()

    return counts
  },

  // ----------------------------------------------------------------------
  // Application Costs
  // ----------------------------------------------------------------------

  async updateApplicationCostsCacheFromApi() {
    await this.refreshApplicationCostsCacheFromApi()
    lastCostsCacheChange.value = new Date()
  },

  async refreshApplicationCostsCacheFromApi() {
    const preferenceStore = usePreferenceStore()
    const preferences = await preferenceStore.getPreferences()
    const results = await _getApplicationCosts({
      targetCurrencyCode: preferences.currency_code,
    })

    if (!results.error && results.data && typeof results.data === 'object') {
      await this.bulkInsertApplicationCosts(results.data.software_costs)
      totalAppCosts.value = results.data.total
    }

    return results
  },

  async bulkInsertApplicationCosts(counts: Record<string, SoftwareCostOut>) {
    const serializedCosts = Object.entries(counts).map(
      ([softwareId, cost]) => ({
        ...serializeApplicationCosts(softwareId, cost),
      })
    )

    if (serializedCosts.length === 0) {
      return counts
    }

    await db
      .insertInto('application_costs')
      .values(serializedCosts)
      .onConflict((oc) =>
        oc.column('_id').doUpdateSet(createUpdateSet(serializedCosts[0]))
      )
      .execute()

    return counts
  },

  async getApplicationCosts(): Promise<Record<string, SoftwareCostOut>[]> {
    const query = db.selectFrom('application_costs').selectAll()

    const results = await query.execute()

    return results.map((result) => deserializeApplicationCosts(result))
  },

  useApplicationsCosts() {
    const costs = ref<Record<string, SoftwareCostOut>[]>()

    const fetchCosts = async () => {
      costs.value = await this.getApplicationCosts()
    }

    const stopWatch = watch(
      [() => lastCostsCacheChange],
      async () => {
        await fetchCosts()
      },
      { deep: true }
    )

    fetchCosts()

    return {
      costs,
      totalAppCosts,
      unsubscribe: stopWatch,
      lastCacheUpdate: lastCostsCacheChange,
    }
  },

  async reset() {
    await db.schema.dropTable('application_costs').ifExists().execute()
    await db.schema.dropTable('application_usercount').ifExists().execute()
    await db.schema.dropTable('application').ifExists().execute()

    await createApplicationTable()
    await createApplicationUserCountTable()
    await createApplicationCostsTable()

    totalAppCosts.value = undefined
    lastCacheChange.value = undefined
    lastCostsCacheChange.value = undefined
  },
}
